class Upsell extends HTMLElement {
  constructor() {
    super();

    this.ENABLE_VARIANT_SYNC = false;
    this.formId = this.dataset.formId;
    this.stickyForms = Array.from(
      document.querySelectorAll('form[id*="product-form-sticky"]')
    );
    this.quantityInputsByIndex = new Map();
    this.idInputsByIndex = new Map();
    this.onStorageUpdatedBound = this.syncBadgesWithInputs.bind(this);
    this.variantChangeTimeout = null;
    this.initialSyncTimeout = null;
    this.onVariantChangeBound = null;
    this.onProductRerenderBound = null;
    this.handleFormSubmitBound = null;

    this.createMissingHiddenInputs();
    this.refreshInputCaches();
    this.init();
    this.setupVariantMatching();
    this.setupBadgeSupport();
    this.setupAddToCartIntercept();
  }

  disconnectedCallback() {
    if (this.handleFormSubmitBound) {
      const form = document.getElementById(this.formId);
      form?.removeEventListener("submit", this.handleFormSubmitBound, true);
      this.handleFormSubmitBound = null;
    }

    document.removeEventListener(
      "upsell:storage-updated",
      this.onStorageUpdatedBound
    );

    if (this.onVariantChangeBound) {
      document.removeEventListener("variant:change", this.onVariantChangeBound);
      this.onVariantChangeBound = null;
    }

    if (this.onProductRerenderBound) {
      document.removeEventListener(
        "product:rerender",
        this.onProductRerenderBound
      );
      this.onProductRerenderBound = null;
    }

    if (this.initialSyncTimeout) {
      clearTimeout(this.initialSyncTimeout);
      this.initialSyncTimeout = null;
    }

    if (this.variantChangeTimeout) {
      clearTimeout(this.variantChangeTimeout);
      this.variantChangeTimeout = null;
    }
  }

  init() {
    this.querySelectorAll("input[name='upsell']").forEach((item, index) => {
      item.dataset.index = index;
      item.addEventListener("change", (event) => {
        this.handleChange(event.target);
      });

      this.updateQuantityForAllForms(index, item.checked);
    });
  }

  setupBadgeSupport() {
    document.addEventListener("upsell:storage-updated", this.onStorageUpdatedBound);

    // Esperamos a que el resto de componentes termine de hidratar antes del primer sync.
    this.initialSyncTimeout = setTimeout(() => {
      this.syncBadgesWithInputs();
      this.initialSyncTimeout = null;
    }, 250);
  }

  syncBadgesWithInputs() {
    const addedProducts = this.getAddedProductsFromBadges();
    const addedProductIds = new Set(addedProducts.map((item) => item.productId));

    addedProducts.forEach((item) => {
      const { variantId, index } = item;

      const idInputs = this.idInputsByIndex.get(String(index)) || [];
      idInputs.forEach((input) => {
        if (variantId) {
          input.value = variantId;
        }
      });

      this.updateQuantityForAllForms(index, true);
    });

    const allCheckboxes = this.querySelectorAll("input[name='upsell']");
    allCheckboxes.forEach((checkbox, index) => {
      const productId = checkbox.closest("[data-product-id]")?.dataset.productId;
      const hasBadge = productId ? addedProductIds.has(productId) : false;

      if (!hasBadge && !checkbox.checked) {
        this.updateQuantityForAllForms(index, false);
      }
    });
  }

  getAddedProductsFromBadges() {
    const STORAGE_KEY = "upsell_added_products";
    let addedProductIds = [];

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      if (Array.isArray(parsed)) {
        addedProductIds = parsed;
      }
    } catch (e) {
      console.error("Error leyendo localStorage:", e);
    }

    const result = [];

    addedProductIds.forEach((productId) => {
      const badge = document.querySelector(
        `[data-badge-type="added"][data-product-id="${productId}"][data-is-added="true"]`
      );
      if (!badge) {
        return;
      }

      const upsellItem = badge.closest("[data-current-variant-id]");
      if (!upsellItem) {
        return;
      }

      const variantId = upsellItem.dataset.currentVariantId;
      const checkbox = upsellItem.querySelector('input[name="upsell"]');
      const index = checkbox ? checkbox.dataset.index : null;

      if (variantId && index !== null) {
        result.push({
          productId,
          variantId,
          index: parseInt(index, 10),
        });
      }
    });

    return result;
  }

  handleChange(target) {
    const checked = target.checked;
    const index = target.dataset.index;
    this.updateQuantityForAllForms(index, checked);
  }

  createMissingHiddenInputs() {
    const upsellCheckboxes = this.querySelectorAll("input[name='upsell']");

    this.stickyForms.forEach((stickyForm) => {
      upsellCheckboxes.forEach((checkbox, index) => {
        const idInputName = `items[${index}][id]`;
        if (!stickyForm.querySelector(`input[name="${idInputName}"]`)) {
          const idInput = document.createElement("input");
          idInput.type = "hidden";
          idInput.name = idInputName;
          idInput.value = checkbox.value;
          idInput.dataset.upsellItem = index;
          stickyForm.appendChild(idInput);
        }

        const quantityInputName = `items[${index}][quantity]`;
        if (!stickyForm.querySelector(`input[name="${quantityInputName}"]`)) {
          const quantityInput = document.createElement("input");
          quantityInput.type = "hidden";
          quantityInput.name = quantityInputName;
          quantityInput.value = "0";
          quantityInput.dataset.upsellQuantity = index;
          stickyForm.appendChild(quantityInput);
        }
      });
    });
  }

  refreshInputCaches() {
    this.quantityInputsByIndex.clear();
    this.idInputsByIndex.clear();

    const upsellCheckboxes = this.querySelectorAll("input[name='upsell']");
    upsellCheckboxes.forEach((_, index) => {
      const idInputName = `items[${index}][id]`;
      const quantityInputName = `items[${index}][quantity]`;

      document
        .querySelectorAll(`input[name="${idInputName}"]`)
        .forEach((input) => this.cacheInput(this.idInputsByIndex, index, input));

      document
        .querySelectorAll(`input[name="${quantityInputName}"]`)
        .forEach((input) =>
          this.cacheInput(this.quantityInputsByIndex, index, input)
        );
    });
  }

  cacheInput(cacheMap, index, input) {
    const key = String(index);
    if (!cacheMap.has(key)) {
      cacheMap.set(key, []);
    }

    cacheMap.get(key).push(input);
  }

  updateQuantityForAllForms(index, checked) {
    const key = String(index);
    const quantityValue = checked ? "1" : "0";
    let quantityInputs = this.quantityInputsByIndex.get(key) || [];

    if (quantityInputs.length === 0) {
      const quantityInputName = `items[${index}][quantity]`;
      quantityInputs = Array.from(
        document.querySelectorAll(`input[name="${quantityInputName}"]`)
      );
      this.quantityInputsByIndex.set(key, quantityInputs);
    }

    quantityInputs.forEach((input) => {
      input.value = quantityValue;
    });
  }

  setupVariantMatching() {
    if (!this.ENABLE_VARIANT_SYNC) {
      return;
    }

    const initialOptions = this.getCurrentMainProductOptions();
    if (initialOptions.length > 0) {
      this.updateUpsellVariantsLightweight(initialOptions);
    }

    this.onVariantChangeBound = (event) => {
      if (event.detail?.variant) {
        this.handleVariantChangeDebounced(event.detail.variant);
      }
    };

    this.onProductRerenderBound = () => {
      setTimeout(() => {
        const currentOptions = this.getCurrentMainProductOptions();
        if (currentOptions.length > 0) {
          this.updateUpsellVariants(currentOptions);
        }
      }, 100);
    };

    document.addEventListener("variant:change", this.onVariantChangeBound);
    document.addEventListener("product:rerender", this.onProductRerenderBound);
  }

  getCurrentMainProductOptions() {
    const mainOptions = [];

    const productDetailVariation = document.querySelector(
      "product-detail-variation"
    );
    if (productDetailVariation) {
      const realVariantInputs = productDetailVariation.querySelectorAll(
        'input[type="radio"]:checked'
      );
      realVariantInputs.forEach((input) => {
        if (!input.name.includes("variant-group-")) {
          const normalized = this.normalizeOptionValue(input.value);
          mainOptions.push(normalized);
        }
      });

      const realSelects = productDetailVariation.querySelectorAll("select");
      realSelects.forEach((select) => {
        if (select.value && !select.closest("product-detail-variant-group")) {
          const normalized = this.normalizeOptionValue(select.value);
          mainOptions.push(normalized);
        }
      });
    }

    return mainOptions;
  }

  handleVariantChangeDebounced(variant) {
    clearTimeout(this.variantChangeTimeout);
    this.variantChangeTimeout = setTimeout(() => {
      this.handleVariantChange(variant);
    }, 100);
  }

  handleVariantChange(variant) {
    if (!variant) return;

    const selectedOptions =
      variant.selected_options || variant.selectedOptions || [];
    if (selectedOptions.length === 0) return;

    const mainOptions = selectedOptions.map((option) => {
      const value = option.value || option.name || option;
      return this.normalizeOptionValue(value);
    });

    this.updateUpsellVariants(mainOptions);
  }

  updateUpsellVariantsLightweight(mainOptions) {
    const upsellItems = this.querySelectorAll("[data-upsell-matcher]");

    upsellItems.forEach((upsellItem) => {
      const currentVariantId = upsellItem.dataset.currentVariantId;
      const matchingVariantId = this.findMatchingVariant(
        upsellItem,
        mainOptions
      );

      if (matchingVariantId && matchingVariantId !== currentVariantId) {
        this.updateUpsellItemCheckboxOnly(upsellItem, matchingVariantId);
      }
    });
  }

  updateUpsellItemCheckboxOnly(upsellItem, newVariantId) {
    const currentCheckbox = upsellItem.querySelector('input[type="checkbox"]');
    if (!currentCheckbox) return;

    currentCheckbox.value = newVariantId;
    upsellItem.dataset.currentVariantId = newVariantId;
  }

  updateUpsellVariants(mainOptions) {
    const upsellItems = this.querySelectorAll("[data-upsell-matcher]");

    upsellItems.forEach((upsellItem) => {
      const currentVariantId = upsellItem.dataset.currentVariantId;
      const matchingVariantId = this.findMatchingVariant(
        upsellItem,
        mainOptions
      );

      if (matchingVariantId && matchingVariantId !== currentVariantId) {
        this.updateUpsellItem(upsellItem, matchingVariantId);
      }
    });
  }

  findMatchingVariant(upsellItem, mainOptions) {
    const availableVariants = upsellItem.dataset.availableVariants;
    if (!availableVariants) return null;

    const variants = availableVariants.split(";").map((entry) => {
      const [optionsStr, variantId] = entry.split(":");
      const options = optionsStr
        ? optionsStr.split("|").filter((o) => o !== "")
        : [];
      return { options, id: variantId };
    });

    const matchingVariant = variants.find((variant) => {
      return variant.options.some((upsellOption) => {
        return mainOptions.some((mainOption) => {
          return upsellOption.includes(mainOption);
        });
      });
    });

    return matchingVariant ? matchingVariant.id : null;
  }

  updateUpsellItem(upsellItem, newVariantId) {
    const currentCheckbox = upsellItem.querySelector('input[type="checkbox"]');
    if (!currentCheckbox) return;

    currentCheckbox.value = newVariantId;
    upsellItem.dataset.currentVariantId = newVariantId;
  }

  setupAddToCartIntercept() {
    if (!this.formId) return;
    const form = document.getElementById(this.formId);
    if (!form) return;
    this.handleFormSubmitBound = this.handleFormSubmit.bind(this);
    form.addEventListener("submit", this.handleFormSubmitBound, true);
  }

  async handleFormSubmit(event) {
    const checkedCheckboxes = this.querySelectorAll(
      "input[name='upsell']:checked"
    );
    if (checkedCheckboxes.length === 0) return;

    const form = event.currentTarget;
    const mainVariantInput =
      form.querySelector("input[data-variant-id-input]") ||
      document.querySelector(
        `input[data-variant-id-input][form="${this.formId}"]`
      );
    if (!mainVariantInput?.value) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const mainVariantId = parseInt(mainVariantInput.value, 10);
    const compareAtInput = form.querySelector(
      'input[name="properties[_compare_at_price]"]'
    );
    const mainItem = { id: mainVariantId, quantity: 1 };
    if (compareAtInput) {
      mainItem.properties = { _compare_at_price: compareAtInput.value };
    }
    const items = [mainItem];

    checkedCheckboxes.forEach((checkbox) => {
      const index = checkbox.dataset.index;
      const idInput = document.querySelector(
        `input[data-upsell-item="${index}"][form="${this.formId}"]`
      );
      const variantId = parseInt(idInput?.value || checkbox.value, 10);
      if (variantId) items.push({ id: variantId, quantity: 1 });
    });

    const submitButtons = Array.from(form.elements).filter(
      (el) => el.type === "submit"
    );
    submitButtons.forEach((btn) => btn.setAttribute("aria-busy", "true"));

    try {
      const root = window.Shopify?.routes?.root || "/";
      const sectionsToBundle = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(","),
          sections_url: `${root}variants/${mainVariantId}`,
        }),
      });

      const responseJson = await response.json();

      if (response.ok) {
        const cartContent = await (
          await fetch(`${root}cart.js`)
        ).json();
        cartContent["sections"] = responseJson["sections"];

        form.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: responseJson.hasOwnProperty("items")
                ? responseJson["items"]
                : [responseJson],
              cart: cartContent,
            },
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { baseEvent: "variant:add", cart: cartContent },
          })
        );
      } else {
        form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: responseJson["description"] },
          })
        );
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      }
    } catch (e) {
      // Fallback: re-submit normally without upsell intercept
      form.removeEventListener("submit", this.handleFormSubmitBound, true);
      form.requestSubmit();
      form.addEventListener("submit", this.handleFormSubmitBound, true);
    } finally {
      submitButtons.forEach((btn) => btn.removeAttribute("aria-busy"));
    }
  }

  normalizeOptionValue(value) {
    return value
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }
}

if (!window.customElements.get("kc-upsell")) {
  window.customElements.define("kc-upsell", Upsell);
}
